import React from 'react';
import { Button, ErrorState, Badge } from '@parsina/framework';
import { C } from '../../../constants/theme';
import { LTR_TEXT } from '../../../i18n/I18nProvider.jsx';
import Odontogram from '../../../components/Odontogram/Odontogram.jsx';
import ToothDrawer from '../../../components/Odontogram/ToothDrawer.jsx';
import { exportChartPng } from '../../../components/Odontogram/exportChart.js';
import { useDentalChart, useToothHistory } from '../../../hooks/useDentalChart.js';
import { useTreatmentPlans } from '../../../hooks/useVisit.js';
import ToothHistory from '../../../components/Odontogram/ToothHistory.jsx';
import TreatedBadge from '../../../components/Odontogram/TreatedBadge.jsx';
import {
  planTeethIndex, outstandingPlanTeeth, treatedByFdi, wholeMouthRecords, recordTitle,
} from '../../Visit/Visit.shared.js';
import { ChartTabSkeleton, ToothHistorySkeleton } from '../PatientDetail.skeleton.jsx';
import { BRIDGE_BAR, PLANNED_RING, TOOTH_COLORS, crownRingTint } from '../../../lib/teeth.js';
import {
  crownPath, crownRingPath, fractureGlyph, implantGlyph, postCoreGlyph,
  rootCanalGlyph, veneerArcPath,
} from '../../../components/Odontogram/geometry.js';

// چارت دندان — the odontogram tab of the patient workspace.
//
// Layout: summary strip, the arch, a legend. The legend is not decoration —
// the chart encodes clinical meaning in colour, and a colour-coded record with
// no key is unreadable to anyone but its author.

const Stat = ({ label, value, tone }) => (
  <div style={{
    background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10,
    padding: '10px 14px', minWidth: 84,
  }}>
    <div style={{ fontSize: 11, color: C.textSecondary, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: tone || C.navy }} {...LTR_TEXT}>
      {value}
    </div>
  </div>
);

const LegendItem = ({ swatch, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{
      width: 12, height: 12, borderRadius: 3, display: 'inline-block',
      background: swatch.fill, border: `1.5px solid ${swatch.stroke}`,
    }} />
    <span style={{ fontSize: 11.5, color: C.textSecondary }}>{label}</span>
  </div>
);

// The condition-glyph legend. Each swatch is drawn by the SAME generator the
// chart uses (on a representative molar, scaled up only for legibility) —
// never redrawn by hand — so the legend cannot drift from the chart.
const LEGEND_FDI = '16';
const LEGEND_POST_CORE = postCoreGlyph(LEGEND_FDI);

const GlyphLegendItem = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <svg
      width={15} height={15} viewBox="-19 -19 38 38" aria-hidden="true"
      style={{ display: 'block', flex: 'none' }}
    >
      {children}
    </svg>
    <span style={{ fontSize: 11.5, color: C.textSecondary }}>{label}</span>
  </div>
);

const LegendSilhouette = () => (
  <path d={crownPath(LEGEND_FDI)} fill="none"
        stroke={TOOTH_COLORS.untouched.stroke} strokeWidth={1.5} />
);

export default function ChartTab({ patientId, clinicId, t, formatDate }) {
  const { teeth, byFdi, summary, loading, error, saving, record, reload } =
    useDentalChart({ patientId, clinicId });

  // ── The plan and the treatments, on the RECORD's chart ──────────────────
  //
  // This is the chart a dentist opens between visits, and it was the one that
  // showed the LEAST: no planned ring, no sign of any treatment, and no tooth
  // history at all. The visit's chart had the ring; this had nothing, so «چه
  // کاری روی این دندان انجام شده؟» could not be answered from the patient's own
  // record.
  //
  // The SAME hook the visit uses, so the two charts cannot disagree about what
  // is planned or what was done.
  const { plans, records, catalog } = useTreatmentPlans({ patientId, clinicId });
  const planTeeth = React.useMemo(() => planTeethIndex(plans), [plans]);
  const plannedFdis = React.useMemo(() => outstandingPlanTeeth(planTeeth), [planTeeth]);
  const treatedFdis = React.useMemo(
    () => treatedByFdi(records, catalog), [records, catalog],
  );
  const wholeMouth = React.useMemo(() => wholeMouthRecords(records), [records]);

  const [selected, setSelected] = React.useState(null);
  const [focusSurface, setFocusSurface] = React.useState(null);
  const [showPrimary, setShowPrimary] = React.useState('auto');
  const [labelMode, setLabelMode] = React.useState('fdi');
  const [exporting, setExporting] = React.useState(false);
  const [exportFailed, setExportFailed] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);
  const historyId = React.useId();
  const chartBoxRef = React.useRef(null);

  // One read per tooth, and only once its trail is asked for — the drawer
  // opens to CHART, and paying for a history fetch on every tooth a dentist
  // clicks through would make the common act the expensive one.
  const history = useToothHistory({
    patientId,
    toothFdi: selected,
    enabled: showHistory && !!selected,
  });

  // Moving to another tooth folds the trail away: left open, it would show one
  // tooth's history under another tooth's heading for the frame before the new
  // read lands.
  React.useEffect(() => { setShowHistory(false); }, [selected]);

  const toothRecords = React.useMemo(
    () => (selected ? (records || []).filter((r) => r?.toothFdi === selected) : []),
    [records, selected],
  );

  const openTooth = React.useCallback((fdi, surface = null) => {
    setSelected(fdi);
    setFocusSurface(surface);
  }, []);

  // «خروجی تصویر» — the chart as a referral/print artifact. The svg is found
  // by ref rather than threading one through Odontogram's API; exportChartPng
  // clones it, so the live chart is never touched.
  const doExport = async () => {
    setExportFailed(false);
    setExporting(true);
    try {
      const res = await exportChartPng({
        svgEl: chartBoxRef.current?.querySelector('svg'),
        patientId,
        // Patient id + date — an LTR run. The filename date is Gregorian
        // (sortable ASCII); THIS one is what a human reads, so it follows
        // the active locale (Jalaali under fa).
        header: `${patientId} — ${formatDate(Date.now(), 'numeric')}`,
      });
      if (!res.ok) setExportFailed(true);
    } finally {
      setExporting(false);
    }
  };

  // Only THIS tab fetches after the record is up, so only this tab body
  // shimmers — in its own shape: the stat strip, the arch at its aspect, the
  // legend band. A centred spinner is banned (DESIGN.md §"Loading & skeleton").
  if (loading) {
    return <ChartTabSkeleton label={t('common.loading')} />;
  }

  if (error) {
    return (
      <ErrorState
        title={t('chart.error.title')}
        description={t('chart.error.body')}
        action={<Button onClick={reload}>{t('common.actions.retry')}</Button>}
      />
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap',
        alignItems: 'center', marginBottom: 16,
      }}>
        <Stat label={t('chart.summary.charted')} value={summary?.chartedTeeth ?? 0} />
        <Stat label={t('chart.summary.decayed')} value={summary?.decayed ?? 0}
              tone={summary?.decayed ? '#dc2626' : undefined} />
        <Stat label={t('chart.summary.restored')} value={summary?.restored ?? 0}
              tone={summary?.restored ? '#2563eb' : undefined} />
        <Stat label={t('chart.summary.missing')} value={summary?.missing ?? 0} />
        {/* DMFT is the index every dentist knows; spelling it out in the label
            avoids it reading as an unexplained acronym. */}
        <Stat label={t('chart.summary.dmft')} value={summary?.dmft ?? 0} />

        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8 }}>
          <Button
            variant="outline"
            onClick={() => setShowPrimary((v) => (v === true ? 'auto' : true))}
          >
            {showPrimary === true ? t('chart.hidePrimary') : t('chart.showPrimary')}
          </Button>
          <Button
            variant="outline"
            onClick={() => setLabelMode((m) => (m === 'fdi' ? 'both' : 'fdi'))}
          >
            {labelMode === 'both' ? t('chart.hideUniversal') : t('chart.showUniversal')}
          </Button>
          <Button variant="outline" onClick={doExport} disabled={exporting}>
            {t('chart.export.button')}
          </Button>
        </div>
      </div>

      {exportFailed && (
        // Same tone as the drawer's save error — an actionable failure, not a
        // crash: the browser simply cannot rasterize (exportChart.js guards).
        <div role="alert" style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 8,
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#b91c1c', fontSize: 12.5,
        }}>
          {t('chart.export.error')}
        </div>
      )}

      <div ref={chartBoxRef} style={{
        background: '#fff', border: `1px solid ${C.border}`,
        borderRadius: 12, padding: '8px 4px 4px',
      }}>
        <Odontogram
          teeth={teeth}
          selected={selected}
          highlight={plannedFdis}
          treated={treatedFdis}
          planned={planTeeth}
          onSelect={(fdi) => openTooth(fdi)}
          onSurfaceSelect={(fdi, surface) => openTooth(fdi, surface)}
          showPrimary={showPrimary}
          labelMode={labelMode}
          t={t}
          formatDate={formatDate}
        />
      </div>

      {/* ── «کل دهان» ──────────────────────────────────────────────────────
          A جرم‌گیری belongs to no tooth, so it can carry no badge; marking all
          32 would bury the teeth that actually had work. One line beneath the
          arch instead — because it is real work, and without it this screen
          says it never happened. */}
      {wholeMouth.length > 0 && (
        <div
          data-testid="chart-whole-mouth"
          style={{
            marginTop: 8, fontSize: 11.5, color: C.textSecondary,
            display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline',
          }}
        >
          <span style={{ color: C.textMuted }}>{t('chart.wholeMouth.title')}{t('common.punct.colon')}</span>
          {wholeMouth.slice(0, 3).map((r, i) => (
            <span key={r.id}>
              {i > 0 ? '· ' : ''}{recordTitle(r, catalog) || t('visit.plan.untitledItem')}
            </span>
          ))}
          {wholeMouth.length > 3 && (
            <span style={{ color: C.textMuted }}>
              {t('chart.tooltip.andMore', { count: wholeMouth.length - 3 })}
            </span>
          )}
        </div>
      )}

      <div style={{
        display: 'flex', gap: 14, flexWrap: 'wrap',
        marginTop: 12, padding: '10px 12px',
        background: '#f8fafc', borderRadius: 10,
      }}>
        <LegendItem swatch={TOOTH_COLORS.caries} label={t('chart.surfaceState.caries')} />
        {/* Beside the surface red, because the pair is the point: the saturated
            swatch is ONE facet, the pale field is the whole tooth. */}
        <LegendItem swatch={TOOTH_COLORS.caries_tooth} label={t('chart.legend.wholeToothCaries')} />
        <LegendItem swatch={TOOTH_COLORS.restoration} label={t('chart.surfaceState.restoration')} />
        <LegendItem swatch={TOOTH_COLORS.recurrent_caries} label={t('chart.surfaceState.recurrent_caries')} />
        <LegendItem swatch={TOOTH_COLORS.temporary} label={t('chart.surfaceState.temporary')} />
        <LegendItem swatch={TOOTH_COLORS.sealant} label={t('chart.surfaceState.sealant')} />
        <LegendItem swatch={TOOTH_COLORS.missing} label={t('chart.presence.extracted')} />
        <LegendItem swatch={TOOTH_COLORS.untouched} label={t('chart.notCharted')} />

        <GlyphLegendItem label={t('chart.conditionName.crown')}>
          <LegendSilhouette />
          <path d={crownRingPath(LEGEND_FDI)} fill="none"
                stroke={crownRingTint(null)} strokeWidth={2.6} />
        </GlyphLegendItem>
        <GlyphLegendItem label={t('chart.conditionName.root_canal')}>
          <LegendSilhouette />
          <polygon points={rootCanalGlyph(LEGEND_FDI)}
                   fill={TOOTH_COLORS.root_canal.stroke} transform="scale(2)" />
        </GlyphLegendItem>
        <GlyphLegendItem label={t('chart.conditionName.implant')}>
          <LegendSilhouette />
          <g transform="scale(1.8)">
            {implantGlyph(LEGEND_FDI).map((s, i) => (
              <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                    stroke={TOOTH_COLORS.implant.stroke}
                    strokeWidth={2} strokeLinecap="round" />
            ))}
          </g>
        </GlyphLegendItem>
        <GlyphLegendItem label={t('chart.conditionName.post_core')}>
          <LegendSilhouette />
          <g transform="scale(1.8)">
            <line x1={LEGEND_POST_CORE.bar.x1} y1={LEGEND_POST_CORE.bar.y1}
                  x2={LEGEND_POST_CORE.bar.x2} y2={LEGEND_POST_CORE.bar.y2}
                  stroke={TOOTH_COLORS.post_core.stroke}
                  strokeWidth={2} strokeLinecap="round" />
            <circle cx={LEGEND_POST_CORE.dot.cx} cy={LEGEND_POST_CORE.dot.cy}
                    r={LEGEND_POST_CORE.dot.r}
                    fill={TOOTH_COLORS.post_core.stroke} />
          </g>
        </GlyphLegendItem>
        <GlyphLegendItem label={t('chart.conditionName.veneer')}>
          <LegendSilhouette />
          <path d={veneerArcPath(LEGEND_FDI)} fill="none"
                stroke={TOOTH_COLORS.restoration.stroke}
                strokeWidth={3.5} strokeLinecap="round" />
        </GlyphLegendItem>
        <GlyphLegendItem label={t('chart.conditionName.fracture')}>
          <LegendSilhouette />
          <polyline points={fractureGlyph(LEGEND_FDI)} fill="none"
                    stroke={TOOTH_COLORS.fractured.stroke} strokeWidth={2} />
        </GlyphLegendItem>
        {/* ── The two ANNOTATIONS ───────────────────────────────────────
            Below the clinical vocabulary and after it, because neither is a
            fact about the tooth: the ring says somebody INTENDS something here
            and the badge says somebody DID something here. Both are drawn by
            the same code the chart draws them with — a legend that redraws a
            mark by hand is a legend that drifts. */}
        <GlyphLegendItem label={t('chart.legend.planned')}>
          <LegendSilhouette />
          <path d={crownPath(LEGEND_FDI)} fill="none" {...PLANNED_RING} />
        </GlyphLegendItem>
        <GlyphLegendItem label={t('chart.legend.treated')}>
          <LegendSilhouette />
          <g transform="scale(2.4)">
            <TreatedBadge count={1} />
          </g>
        </GlyphLegendItem>

        <GlyphLegendItem label={t('chart.legend.bridge')}>
          {/* The bar has no shape generator — the mark IS a bar along the arch
              (bridgeSpans decides where). Its stroke RECIPE is still shared:
              BRIDGE_BAR is the same constant the chart spreads, so the swatch
              shows exactly the width/opacity the chart draws. */}
          <line x1={-15} y1={0} x2={15} y2={0} {...BRIDGE_BAR} />
        </GlyphLegendItem>

        <span style={{ marginInlineStart: 'auto', fontSize: 11.5, color: C.textSecondary }}>
          {t('chart.hint')}
        </span>
      </div>

      <ToothDrawer
        open={!!selected}
        fdi={selected}
        state={selected ? byFdi[selected] : null}
        focusSurface={focusSurface}
        saving={saving}
        onSave={record}
        onClose={() => { setSelected(null); setFocusSurface(null); }}
        history={(
          /* «تاریخچه دندان» — the half of the record the chart cannot draw.
             The folded state says a tooth is crowned; it does not say when, by
             whom, or what was there before, and on THIS screen — the one
             opened between visits, with no encounter around it — that is the
             whole question. */
          <div style={{ marginBottom: 14 }}>
            <Button
              variant="subtle"
              aria-expanded={showHistory}
              aria-controls={historyId}
              onClick={() => setShowHistory((v) => !v)}
            >
              {t('visit.chart.history')}
            </Button>

            {showHistory && (
              <div id={historyId} style={{ marginTop: 8 }}>
                {history.loading
                  ? <ToothHistorySkeleton label={t('common.loading')} />
                  : (
                    <ToothHistory
                      fdi={selected}
                      events={history.events}
                      records={toothRecords}
                      catalog={catalog}
                      error={!!history.error}
                      t={t}
                      formatDate={formatDate}
                    />
                  )}
              </div>
            )}
          </div>
        )}
        t={t}
      />
    </div>
  );
}
